import { useCallback, useState, type ReactNode } from 'react';
import { cn } from '@strivelabs/ui-core/lib/utils';

type LoadState = 'loading' | 'loaded' | 'failed';

interface LibraryImageProps {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  /** Drawn in the image's place when it cannot be loaded. */
  fallback: ReactNode;
  /** Load straight away rather than when it nears the screen, for an upload's local preview. */
  eager?: boolean;
  className?: string;
}

/**
 * An image that fills its box: a soft pulse until it arrives, then a fade in,
 * so tiles come in quietly rather than painting top-down. The box is sized by
 * the caller from the image's known shape, so nothing moves when it lands.
 *
 * Off-screen images wait for the browser's lazy loading. One that cannot be
 * loaded draws `fallback` instead of a broken-image glyph.
 */
export const LibraryImage = ({
  src, srcSet, sizes, alt, fallback, eager = false, className,
}: LibraryImageProps) => {
  // Which source finished, rather than a flag reset on every new source: a
  // reset in an effect runs after paint, and so can land after a cached
  // image's `load` and leave it hidden for good.
  const [ loadedSrc, setLoadedSrc ] = useState<string | null>(null);
  const [ failedSrc, setFailedSrc ] = useState<string | null>(null);
  let state: LoadState = 'loading';
  if (failedSrc === src) {
    state = 'failed';
  } else if (loadedSrc === src) {
    state = 'loaded';
  }

  // An image already in the cache can finish before React attaches `onLoad`.
  const measure = useCallback((image: HTMLImageElement | null) => {
    if (image?.complete && image.naturalWidth > 0) {
      setLoadedSrc(src);
    }
  }, [ src ]);

  if (state === 'failed') {
    return <>{fallback}</>;
  }

  return (
    <span className={cn('relative block size-full overflow-hidden', className)}>
      {state === 'loading' ? (
        <span aria-hidden className="absolute inset-0 animate-pulse bg-v2-list-tabbar motion-reduce:animate-none" />
      ) : null}
      <img
        ref={measure}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        className={cn(
          'block size-full object-cover transition-opacity duration-v2-slow ease-v2-out motion-reduce:transition-none',
          state === 'loaded' ? 'opacity-100' : 'opacity-0',
        )}
        onLoad={() => setLoadedSrc(src)}
        onError={() => setFailedSrc(src)}
      />
    </span>
  );
};
